import { FormattedMessage } from 'react-intl';

import { Button } from '@src/components/atoms/inputs/button';
import { Spinner } from '@src/components/atoms/spinner';
import { ScrollNavigation } from '@src/components/molecules/scroll-navigation';
import { TripSection } from '@src/components/pages/my-trips/components/past-canceled-trips/components/trip-section/trip-section';
import { usePastCanceledTripsQuery } from '@src/components/pages/my-trips/use-past-canceled-trips-query';
import { useCmsContent } from '@src/contexts/cms-content-context';
import { TripListItem } from '@src/types/api/models';

import styles from './past-canceled-trips.module.css';

export const PastCanceledTrips = () => {
  const content = useCmsContent();
  const { isLoading, isSuccess, data, hasNextPage, fetchNextPage, isFetchingNextPage } = usePastCanceledTripsQuery();

  // Show spinner when loading the first page
  if (isLoading) {
    return (
      <div className={styles.loadingWrapper}>
        <Spinner />
      </div>
    );
  }

  if (!isSuccess || !data) {
    return <></>;
  }

  const pastAndCanceledTrips = data.pages.flatMap(({ data }) => data);
  const pastTrips: TripListItem[] = pastAndCanceledTrips.filter(({ status }) => status === 'CheckedOut');
  const canceledTrips: TripListItem[] = pastAndCanceledTrips.filter(({ status }) => status === 'Canceled');

  // Nothing to show: no past/canceled trips on the loaded pages, and no further pages to fetch.
  if (!pastTrips.length && !canceledTrips.length && !hasNextPage) {
    return <></>;
  }

  const currentPage = data.pages[data.pages.length - 1];
  const remainingResults = Math.min(currentPage.limit, currentPage.total - pastAndCanceledTrips.length);

  return (
    <div className={styles.wrapper}>
      <ScrollNavigation isSticky containerClassname={styles.scrollPaddingContainer}>
        {!!pastTrips.length && (
          <TripSection
            key="past-trips"
            slug="past-trips"
            tabText={content.tabPast}
            title={content.pastTrips}
            trips={pastTrips}
          />
        )}

        {!!canceledTrips.length && (
          <TripSection
            key="cancelled-trips"
            slug="cancelled-trips"
            tabText={content.tabCanceled}
            title={content.canceledTrips}
            trips={canceledTrips}
          />
        )}
      </ScrollNavigation>

      {hasNextPage && (
        <div className={styles.buttonWrapper}>
          <Button onClick={() => fetchNextPage()} isLoading={isFetchingNextPage} variant="secondary">
            <FormattedMessage
              id="showNMore"
              defaultMessage={content.showNMore}
              values={{
                count: remainingResults,
              }}
            />
          </Button>
        </div>
      )}
    </div>
  );
};
